/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { API } from 'middleware/api';

export const UPDATE_PROFILE_REQUEST = 'UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_FAILURE = 'UPDATE_PROFILE_FAILURE';

/**
 * Create action Object that is used to update a users profile.
 *
 * @param {string} token - JWT for request authentification.
 * @param {Object} profile - The profile Object, includes all properties for different types of users.
 * @param {string} profile.role_type - User role.
 * @param {string} profile.firstname - Users firstname.
 * @param {string} profile.lastname - Users lastname.
 * @param {string} profile.phone_number - Users phone number, including country code.
 * @param {string} profile.email  - Users email address.
 * @param {string} profile.language - Users preferred language.
 * @param {string} profile.title - Job title at company.
 * @param {string} profile.location_city - City in which the user lives/works.
 * @param {string} profile.location_country - Country in which the user lives/works.
 * @param {string} profile.talent_bio - A short biography of the talent.
 * @param {string} profile.earlier_jobs - A short description of this talent’s earlier jobs outside Ksting.
 * @param {string} profile.instagram_url - Url of the talent’s instagram profile.
 * @param {string} profile.twitter_url - Url of the talent’s twitter feed.
 * @param {string} profile.facebook_url - Url of the talent’s facebook page.
 * @param {('MALE'|'FEMALE')} profile.model_gender - The model’s gender.
 * @param {string} profile.model_eye_color - The model’s eye color.
 * @param {date} profile.model_birth_date - The model’s birthdate.
 * @param {string} profile.model_shoe_sizes - The model’s shoe size.
 * @param {string} profile.model_clothing_sizes - The model’s clothing size.
 * @param {string} profile.model_has_tattoos_piercings - If the model has tattoos/piercings.
 * @param {string} profile.model_height - The model’s height in cm.
 * @param {string} profile.model_chest - The model’s chest size in cm.
 * @param {string} profile.model_waist - The model’s waist size in cm.
 * @param {string} profile.model_hips - The model’s hips size in cm.
 * @param {string} profile.model_weight - The model’s weight in kg.
 * @param {('CAUCASIAN'|'ASIAN'|'AFRICAN'|'INDIAN'|'LATIN')} profile.model_ethnic_type - The model’s ethnic type.
 *
 * @return {Object}
 */
export const updateProfile = (token, profile) => ({
  [API]: {
    endpoint: '/user/profile/update',
    method: 'POST',
    body: profile,
    token,
    types: [
      UPDATE_PROFILE_REQUEST,
      UPDATE_PROFILE_SUCCESS,
      UPDATE_PROFILE_FAILURE,
    ],
  },
});

export const GET_PROFILE_REQUEST = 'GET_PROFILE_REQUEST';
export const GET_PROFILE_SUCCESS = 'GET_PROFILE_SUCCESS';
export const GET_PROFILE_FAILURE = 'GET_PROFILE_FAILURE';

/**
 * Fetch a users profile, given its slug or client_id/talent_id.
 *
 * @param {string} token - User auth token.
 * @param {string} slug - Slug, talent ID or client ID.
 *
 * @return {Object}
 */
export const getProfile = (token, slug, jobId = null) => ({
  [API]: {
    endpoint: `/user/profile/${slug}`,
    method: 'GET',
    body: jobId
      ? {
          job_id: jobId,
        }
      : null,
    token,
    types: [GET_PROFILE_REQUEST, GET_PROFILE_SUCCESS, GET_PROFILE_FAILURE],
  },
});

export const REQUEST_CODE_REQUEST = 'REQUEST_CODE_REQUEST';
export const REQUEST_CODE_SUCCESS = 'REQUEST_CODE_SUCCESS';
export const REQUEST_CODE_FAILURE = 'REQUEST_CODE_FAILURE';

/**
 * Request a validation code over SMS to the provided phonenumber.
 *
 * @param {string} token - User auth token.
 * @param {Object} data - The data Object.
 * @param {string} data.phone_number - The users phone number.
 *
 * @return {Object}
 */
export const requestCode = (token, data) => ({
  [API]: {
    endpoint: '/user/phone/sendcode',
    method: 'POST',
    body: data,
    token,
    types: [REQUEST_CODE_REQUEST, REQUEST_CODE_SUCCESS, REQUEST_CODE_FAILURE],
  },
});

export const VERIFY_CODE_REQUEST = 'VERIFY_CODE_REQUEST';
export const VERIFY_CODE_SUCCESS = 'VERIFY_CODE_SUCCESS';
export const VERIFY_CODE_FAILURE = 'VERIFY_CODE_FAILURE';

/**
 * Verify the code that was sent by SMS to the users phone.
 *
 * @param {string} token - User auth token.
 * @param {Object} data - The data Object.
 * @param {string} data.code - The code to be verified.
 *
 * @return {Object}
 */
export const verifyCode = (token, data) => ({
  [API]: {
    endpoint: '/user/phone/verifycode',
    method: 'POST',
    body: data,
    token,
    types: [VERIFY_CODE_REQUEST, VERIFY_CODE_SUCCESS, VERIFY_CODE_FAILURE],
  },
});

export const RESET_CODE_REQUEST = 'RESET_CODE_REQUEST';
export const RESET_CODE_SUCCESS = 'RESET_CODE_SUCCESS';
export const RESET_CODE_FAILURE = 'RESET_CODE_FAILURE';

/**
 * Reset action that enables the user to go back and request a new code.
 *
 * @param {string} token - User auth token.
 *
 * @return {Object}
 */
export const resetCode = token => ({
  [API]: {
    endpoint: '/user/phone/reset',
    method: 'POST',
    body: {},
    token,
    types: [RESET_CODE_REQUEST, RESET_CODE_SUCCESS, RESET_CODE_FAILURE],
  },
});

export const ADD_PICTURE_REQUEST = 'ADD_PICTURE_REQUEST';
export const ADD_PICTURE_SUCCESS = 'ADD_PICTURE_SUCCESS';
export const ADD_PICTURE_FAILURE = 'ADD_PICTURE_FAILURE';

/**
 * Add picture to user profile action.
 *
 * @param {string} token - User auth token.
 * @param {Object} data - Picture data object.
 * @param {string} data.role_type - User role.
 * @param {string} data.indexes - picture indexes.
 * @param {string} data.cloudinary_public_ids - picture public_ids.
 *
 * @return {Object}
 */
export const addPicture = (token, data) => ({
  [API]: {
    endpoint: '/user/picture/add',
    method: 'POST',
    body: data,
    token,
    types: [ADD_PICTURE_REQUEST, ADD_PICTURE_SUCCESS, ADD_PICTURE_FAILURE],
  },
});

export const MAKE_DEFAULT_PICTURE_REQUEST = 'MAKE_DEFAULT_PICTURE_REQUEST';
export const MAKE_DEFAULT_PICTURE_SUCCESS = 'MAKE_DEFAULT_PICTURE_SUCCESS';
export const MAKE_DEFAULT_PICTURE_FAILURE = 'MAKE_DEFAULT_PICTURE_FAILURE';

/**
 * Make picture user profile default action.
 *
 * @param {string} token - User auth token.
 * @param {number} index - Picture index.
 * @param {string} roleType - User role.
 * @param {Object} data - Picture data object.
 * @param {string} data.index - picture index.
 *
 * @return {Object}
 */
export const makeDefaultPicture = (token, index, roleType, data) => ({
  [API]: {
    endpoint: `/user/picture/${index}/default?role_type=${roleType}`,
    method: 'POST',
    body: data,
    token,
    types: [
      MAKE_DEFAULT_PICTURE_REQUEST,
      MAKE_DEFAULT_PICTURE_SUCCESS,
      MAKE_DEFAULT_PICTURE_FAILURE,
    ],
  },
});

export const DELETE_PICTURE_REQUEST = 'DELETE_PICTURE_REQUEST';
export const DELETE_PICTURE_SUCCESS = 'DELETE_PICTURE_SUCCESS';
export const DELETE_PICTURE_FAILURE = 'DELETE_PICTURE_FAILURE';

/**
 * Delete picture from user profile action.
 *
 * @param {string} token - User auth token.
 * @param {number} index - Picture index.
 * @param {string} roleType - User role.
 * @param {Object} data - Picture data object.
 * @param {string} data.index - picture index.
 *
 * @return {Object}
 */
export const deletePicture = (token, index, roleType, data) => ({
  [API]: {
    endpoint: `/user/picture/${index}/delete?role_type=${roleType}`,
    method: 'POST',
    body: data,
    token,
    types: [
      DELETE_PICTURE_REQUEST,
      DELETE_PICTURE_SUCCESS,
      DELETE_PICTURE_FAILURE,
    ],
  },
});

export const RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_FAILURE = 'RESET_PASSWORD_FAILURE';

/**
 * Reset action that enables the user to reset their password via SMS.
 *
 * @param {Object} data - Reset data object.
 * @param {string} data.email_or_phone - email address or phone number of the user whose password to reset.
 *
 * @return {Object}
 */
export const resetPassword = data => ({
  [API]: {
    endpoint: '/user/password/sendreset',
    method: 'POST',
    body: data,
    types: [
      RESET_PASSWORD_REQUEST,
      RESET_PASSWORD_SUCCESS,
      RESET_PASSWORD_FAILURE,
    ],
  },
});

export const UPDATE_PASSWORD_REQUEST = 'UPDATE_PASSWORD_REQUEST';
export const UPDATE_PASSWORD_SUCCESS = 'UPDATE_PASSWORD_SUCCESS';
export const UPDATE_PASSWORD_FAILURE = 'UPDATE_PASSWORD_FAILURE';

/**
 * Update action that enables the user to update their password via unique code.
 *
 * @param {Object} data - Reset data object.
 * @param {string} data.reset_code - Unique one-time reset code identifying a user.
 * @param {string} data.password_hash - MD5 hash of the user’s password.
 *
 * @return {Object}
 */
export const updatePassword = data => ({
  [API]: {
    endpoint: '/user/password/update',
    method: 'POST',
    body: data,
    types: [
      UPDATE_PASSWORD_REQUEST,
      UPDATE_PASSWORD_SUCCESS,
      UPDATE_PASSWORD_FAILURE,
    ],
  },
});

export const DELETE_PROFILE_REQUEST = 'DELETE_PROFILE_REQUEST';
export const DELETE_PROFILE_SUCCESS = 'DELETE_PROFILE_SUCCESS';
export const DELETE_PROFILE_FAILURE = 'DELETE_PROFILE_FAILURE';

/**
 * Delete a profile (only for testing)
 *
 * @param {string} token - User auth token.
 *
 * @return {Object}
 */
export const deleteProfile = token => ({
  [API]: {
    endpoint: '/../_dev/deleteprofile',
    method: 'GET',
    body: {},
    token,
    types: [
      DELETE_PROFILE_REQUEST,
      DELETE_PROFILE_SUCCESS,
      DELETE_PROFILE_FAILURE,
    ],
  },
});

export const APPROVE_PROFILE_REQUEST = 'APPROVE_PROFILE_REQUEST';
export const APPROVE_PROFILE_SUCCESS = 'APPROVE_PROFILE_SUCCESS';
export const APPROVE_PROFILE_FAILURE = 'APPROVE_PROFILE_FAILURE';

/**
 * Approve a profile (only for testing)
 *
 * @param {string} token - User auth token.
 *
 * @return {Object}
 */
export const approveProfile = token => ({
  [API]: {
    endpoint: '/../_dev/approve',
    method: 'GET',
    body: {},
    token,
    types: [
      APPROVE_PROFILE_REQUEST,
      APPROVE_PROFILE_SUCCESS,
      APPROVE_PROFILE_FAILURE,
    ],
  },
});

export const REORDER_PICTURES_REQUEST = 'REORDER_PICTURES_REQUEST';
export const REORDER_PICTURES_SUCCESS = 'REORDER_PICTURES_SUCCESS';
export const REORDER_PICTURES_FAILURE = 'REORDER_PICTURES_FAILURE';

/**
 * Update pictures order.
 *
 * @param {string} token - User auth token.
 * @param {Array} data - Array of picture_ids.
 *
 * @return {Object}
 */
export const updatePicturesOrder = (token, data) => ({
  [API]: {
    endpoint: '/user/picture/reorder',
    method: 'POST',
    body: data,
    token,
    types: [
      REORDER_PICTURES_REQUEST,
      REORDER_PICTURES_SUCCESS,
      REORDER_PICTURES_FAILURE,
    ],
  },
});
